/** Poscraft: paymentClass 1 = cash, 4 = card/Stripe. IDs come from each API row. */
export const PAYMENT_CLASS_KEYS = {
  1: 'cash',
  4: 'card',
};

const extractPaymentTypesList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.Data)) return raw.Data;
  if (Array.isArray(raw?.results)) return raw.results;
  if (Array.isArray(raw?.items)) return raw.items;
  return [];
};

/** Poscraft may send boolean true, 1, or "true". Treat explicit false/0 as inactive. */
export const isPoscraftPaymentTypeActive = (row) => {
  const active = row?.active ?? row?.Active;
  if (active === false || active === 0 || String(active).toLowerCase() === 'false') return false;
  return active === true || active === 1 || String(active).toLowerCase() === 'true';
};

export const isPoscraftPaymentTypeOnWebsite = (row) =>
  row?.availableOnWebsite !== false && row?.AvailableOnWebsite !== false;

const toCheckoutPaymentMethod = (key, row) => ({
  key,
  name: String(row?.name || '').trim() || (key === 'cash' ? 'Cash' : 'Card'),
  paymentTypeId: Number(row.paymentTypeId),
  paymentClass: Number(row.paymentClass),
  surcharge: Number(row?.surcharge) || 0,
  companyPaymentTypeId: Number(row?.id) || 0,
});

const readPaymentClass = (row) => Number(row?.paymentClass ?? row?.PaymentClass);

/**
 * Active + website-enabled payment types from Poscraft PaymentTypes API.
 * @param {unknown} raw
 * @returns {Array<{ key: 'cash'|'card', name: string, paymentTypeId: number, paymentClass: number, surcharge: number, companyPaymentTypeId: number }>}
 */
export const normalizeCompanyPaymentTypes = (raw) => {
  const list = extractPaymentTypesList(raw).filter(
    (row) => isPoscraftPaymentTypeActive(row) && isPoscraftPaymentTypeOnWebsite(row)
  );
  const methods = [];

  const cashRow = list.find((row) => readPaymentClass(row) === 1);
  if (cashRow) methods.push(toCheckoutPaymentMethod('cash', cashRow));

  const cardRows = list.filter((row) => readPaymentClass(row) === 4);
  const cardRow =
    cardRows.find((row) => String(row?.configuration || '').includes('publishableKey')) || cardRows[0];
  if (cardRow) methods.push(toCheckoutPaymentMethod('card', cardRow));

  return methods;
};

/** Stripe keys from active card payment type configuration JSON (Poscraft). */
export const parseStripeSettingsFromPaymentTypes = (raw) => {
  const list = extractPaymentTypesList(raw);
  const cardRow =
    list.find(
      (row) =>
        isPoscraftPaymentTypeActive(row) &&
        isPoscraftPaymentTypeOnWebsite(row) &&
        readPaymentClass(row) === 4 &&
        String(row?.configuration || '').includes('publishableKey')
    ) || list.find((row) => isPoscraftPaymentTypeActive(row) && readPaymentClass(row) === 4);

  if (!cardRow?.configuration) return null;
  try {
    const config =
      typeof cardRow.configuration === 'string'
        ? JSON.parse(cardRow.configuration)
        : cardRow.configuration;
    const publishableKey = String(config?.publishableKey || config?.PublishableKey || '').trim();
    const connectAccountId = String(config?.connectAccountId || config?.ConnectAccountId || '').trim();
    if (!publishableKey) return null;
    return { publishableKey, connectAccountId };
  } catch {
    return null;
  }
};

/** Poscraft StripeIntent expects a flat JSON body (Postman shape), not `{ stripePayment: {…} }`. */
export const buildPoscraftStripeIntentBody = (fields) => ({
  customerId: Number(fields.customerId) || 0,
  locationId: Number(fields.locationId) || 0,
  netAmount: Math.round(Number(fields.netAmount || 0) * 100) / 100,
  customerName: String(fields.customerName || '').trim(),
  email: String(fields.email || '').trim(),
  serviceTypeId: Number(fields.serviceTypeId) || 0,
  orderRef: String(fields.orderRef || '').trim(),
});

/** GBP minimum charge in pence (Stripe). */
export const STRIPE_GBP_MIN_MINOR_UNITS = 30;

/** Real charge in pence from a pound/dollar major-unit total. */
export const stripeMinorUnitsFromMajor = (amountMajor) => {
  const major = Number(amountMajor);
  if (!Number.isFinite(major) || major <= 0) return 0;
  return Math.round(major * 100);
};

export const isBelowStripeMinimumCharge = (amountMajor, minMinor = STRIPE_GBP_MIN_MINOR_UNITS) =>
  stripeMinorUnitsFromMajor(amountMajor) < minMinor;

/** When set (> 0), card checkout charges this amount instead of the order total (testing only). */
export const resolveStripeChargeAmountMajor = (orderTotalMajor, testOverrideMajor) => {
  const test = Number(testOverrideMajor);
  if (Number.isFinite(test) && test > 0) {
    return Math.round(test * 100) / 100;
  }
  const order = Number(orderTotalMajor);
  if (!Number.isFinite(order) || order <= 0) return 0;
  return Math.round(order * 100) / 100;
};

/** Extract Stripe client secret from Poscraft StripeIntent response shapes. */
export const parseStripeIntentClientSecret = (payload) => {
  if (!payload || typeof payload !== 'object') return '';
  const data = payload.data ?? payload.Data;
  if (typeof data === 'string') {
    const dataStr = data.trim();
    if (dataStr.includes('_secret_')) return dataStr;
    if (dataStr.startsWith('{')) {
      try {
        return parseStripeIntentClientSecret(JSON.parse(dataStr));
      } catch {
        // fall through
      }
    }
  }
  const dataObj = data && typeof data === 'object' ? data : null;
  const direct =
    payload.clientSecret ||
    payload.ClientSecret ||
    payload.client_secret ||
    dataObj?.clientSecret ||
    dataObj?.ClientSecret ||
    dataObj?.client_secret;
  if (direct) return String(direct).trim();

  const message = String(payload.message || payload.Message || '').trim();
  if (!message) return '';
  if (message.includes('_secret_')) return message;
  if (message.startsWith('{')) {
    try {
      const nested = JSON.parse(message);
      return parseStripeIntentClientSecret(nested);
    } catch {
      return '';
    }
  }
  return '';
};
